import { Children, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";

/**
 * Base UI applies native-button semantics to the rendered element unless `nativeButton={false}`.
 * When auto-promoting an element child to `render`, infer it for intrinsic elements (a `<div>`
 * or `<span>` trigger is not a native button); assume custom components (Button, IconButton)
 * render a native `<button>`.
 *
 * Caveat: that assumption is heuristic — a custom component that renders a `<div>`/`<a>` host
 * will get incorrect button semantics. Such consumers must pass `nativeButton={false}` explicitly.
 */
function isNativeButtonElement(element: ReactElement | undefined): boolean {
  if (!element) {
    return true;
  }
  return typeof element.type !== "string" || element.type === "button";
}

/**
 * React's two context element types. Both read only `value` (provider) or `children` (consumer)
 * off the element, so every other prop cloned onto one is discarded. They are objects rather than
 * symbols, identified by `$$typeof`.
 */
const CONTEXT_ELEMENT_TYPES = new Set<symbol>([Symbol.for("react.context"), Symbol.for("react.consumer")]);

function isContextElementType(type: unknown): boolean {
  if (typeof type !== "object" || type === null) {
    return false;
  }
  const { $$typeof: kind } = type as { $$typeof?: symbol };
  return kind !== undefined && CONTEXT_ELEMENT_TYPES.has(kind);
}

/**
 * Whether an element can carry the props Base UI hands to `render`. React silently drops every
 * prop — `ref`, `className`, `onClick`, ARIA attributes — when cloning one of its built-in
 * wrapper types, so promoting one yields a trigger that cannot be opened by mouse or keyboard.
 * Two families do this: `Fragment`, `Suspense`, `StrictMode` and `Profiler`, which are symbols;
 * and `Context.Provider` / `Context.Consumer`, which are objects carrying a context `$$typeof`.
 * Intrinsic tags (string types), components (function/class), and wrapped components such as
 * `memo`, `forwardRef` and `lazy` (also object types) all accept props, so they stay promotable —
 * which is why context is excluded by identity rather than by rejecting object types wholesale.
 *
 * Exported because `Tooltip`'s `toRenderElement` applies the same test before wrapping — the two
 * promotion paths must agree on what is promotable. `isNativeButtonElement` above stays private
 * precisely because it is a heuristic and should only be reached through `promoteChildren`.
 */
export function canHostProps(element: ReactElement): boolean {
  return typeof element.type !== "symbol" && !isContextElementType(element.type);
}

/**
 * Automatic child-promotion for Base UI parts that must not nest an interactive host inside
 * another one: a single element child becomes Base UI's `render`, so the consumer's element *is*
 * the host. Popover and Tooltip need this — a `<button>` trigger inside Base UI's own `<button>`
 * is invalid markup, not a styling preference. `nativeButton` is inferred from the promoted
 * element unless passed explicitly. `remainingChildren` is what renders inside Base UI's own host
 * when nothing was promoted (a fragment, multiple children, text, or nothing).
 *
 * Parts that promote for styling rather than markup validity want `consumerRenderHost` instead,
 * so the consumer opts in.
 */
export function promoteChildren(
  children: ReactNode,
  nativeButton: boolean | undefined,
): { render?: ReactElement; nativeButton: boolean; remainingChildren?: ReactNode } {
  const render = isValidElement(children) && canHostProps(children) ? children : undefined;
  return {
    ...(render ? { render } : { remainingChildren: children }),
    // Same rule as `consumerRenderHost`: `nativeButton` describes the host, so with nothing
    // promoted it describes the part's own default <button> and the override does not apply.
    nativeButton: render ? nativeButton ?? isNativeButtonElement(render) : true,
  };
}

/**
 * Explicit promotion for trigger-style parts that draw their own chrome: the consumer opts in by
 * passing `render`, and children always render inside the part's own host. This is the `asChild`
 * contract — a single element child stays inside the styled trigger, and only an explicit
 * `render` hands the element the host role along with responsibility for its own border, padding
 * and disabled/placeholder states.
 *
 * Only the *element* form of `render` takes over chrome. A render *callback* supplies the host
 * element and spreads the part's props — `className` included — back onto it, so the part's own
 * chrome still applies.
 */
export function consumerRenderHost<TRender>(
  render: TRender,
  nativeButton: boolean | undefined,
): { render?: TRender; demotedChildren?: ReactNode; nativeButton: boolean; hostOwnsChrome: boolean } {
  // An element that cannot host props leaves the part with no interactive host at all: React drops
  // the ref, handlers and ARIA Base UI hands it, so nothing opens by mouse or keyboard. Hand it
  // back as children instead, which is where a non-promoting child already lands — the content
  // survives and the part keeps its own host rather than gaining a third promotion rule.
  if (isValidElement(render) && !canHostProps(render)) {
    return { demotedChildren: render, hostOwnsChrome: false, nativeButton: true };
  }

  const element = isValidElement(render) ? render : undefined;
  const hasConsumerHost = element !== undefined || typeof render === "function";
  return {
    render,
    hostOwnsChrome: element !== undefined,
    // `nativeButton` describes whichever element ends up as the host. With no consumer host that
    // is the part's own default host, so an explicit override has nothing to describe and Base
    // UI's own default applies — otherwise it configures a real <button> as non-native and swaps
    // native `disabled` for `aria-disabled`.
    nativeButton: hasConsumerHost ? nativeButton ?? isNativeButtonElement(element) : true,
  };
}

/**
 * Children for a trigger-style part that supplies its own default content, falling back when the
 * consumer passed nothing renderable.
 *
 * Neither `children ?? fallback` nor `children || fallback` expresses this: `??` only catches
 * `null`/`undefined`, so `<Trigger>{cond && <X/>}</Trigger>` with a false `cond` renders an empty
 * trigger, and `||` swallows a legitimate `0`.
 */
export function childrenOrFallback(children: ReactNode, fallback: ReactNode): ReactNode {
  return rendersSomething(children) ? children : fallback;
}

/**
 * Whether `children` puts anything on the page. `Children.toArray` drops what React renders
 * nothing for — `null`, `undefined`, booleans, empty arrays — but keeps empty strings, and counts
 * a fragment as a single element without looking inside it. A fragment wrapping only non-rendering
 * children therefore has to be opened up, or `<Trigger><>{false}</></Trigger>` reads as content
 * and suppresses the fallback. Only fragments can be inspected this way: a component that returns
 * `null` is indistinguishable from one that renders until it runs.
 */
function rendersSomething(children: ReactNode): boolean {
  return Children.toArray(children).some((child) => {
    if (child === "") {
      return false;
    }
    if (isValidElement(child) && child.type === Fragment) {
      const { children: fragmentChildren } = child.props as { children?: ReactNode };
      return rendersSomething(fragmentChildren);
    }
    return true;
  });
}
